/**
 * Pure planner for the combined page's "Download All Files" ZIP
 * (COMBINED-V2-DATA-MAP §3 / useDownloads.downloadHousehold). Splitting the
 * plan out of the hook keeps the testable substance — member ordering, the
 * in-ZIP paths, and the resource URLs — a pure function (house rule: pure
 * derivations carry the tests, the hook stays thin I/O glue). Mirrors v1's
 * combined branch (v1 handleDownloadAllZip, v1:6286-6314): the family report
 * at the root, then one folder per member ordered primary → co-applicant →
 * guarantor, each with that member's individual + applicant-info + income/rent
 * PDFs and their ETL sheet (one ETL per member, keyed by the member's own
 * applicationId — not primary-only).
 */

export interface HouseholdMemberRef {
	applicationId: string
	displayName: string
	applicationType: 'primary' | 'co_applicant' | 'guarantor'
}

/** One file to fetch and place in the ZIP. `path` may contain a `/` — JSZip
 * reads that as a folder. A null blob at fetch time is skipped (best-effort,
 * v1 parity). */
export interface HouseholdZipEntry {
	path: string
	url: string
}

export interface HouseholdZipPlanInput {
	members: HouseholdMemberRef[]
	/** The entry application — anchors the family report (data-map §1). */
	entryApplicationId: string
	groupId: string | null
	propertyId: string
	/** downloads.familyReport — false for pre-screen groups / solo (Task 6),
	 * in which case no family report is added. */
	includeFamilyReport: boolean
}

/** v1 zipTypeOrder (v1:6293-6294): primary first, guarantor last. */
function typeRank(type: HouseholdMemberRef['applicationType']): number {
	return type === 'primary' ? 0 : type === 'guarantor' ? 2 : 1
}

/** Folder-safe member name: alphanumerics only, the rest collapsed to `-`,
 * lowercased (the hook's existing `safeName`). */
export function memberFolderName(displayName: string): string {
	return (displayName || 'applicant')
		.replace(/[^a-zA-Z0-9]/g, '-')
		.toLowerCase()
}

// Both report types here (family, individual) and the verification PDFs are
// ported to the browser-free react-pdf engine, so the ZIP fetches that engine.
function reportUrl(params: Record<string, string>): string {
	return `/resources/generate-report-pdf?${new URLSearchParams({
		...params,
		engine: 'react-pdf',
	})}`
}

function verificationUrl(
	applicationId: string,
	propertyId: string,
	type: string,
): string {
	return `/resources/download-verification-pdf?${new URLSearchParams({
		applicationId,
		propertyId,
		type,
		engine: 'react-pdf',
	})}`
}

/** The member's ETL sheet (download-etl-sheet is keyed by applicationId, so each
 * member exports their own row — primary, co-applicant, and guarantor alike). */
function etlUrl(applicationId: string, propertyId: string): string {
	return `/resources/download-etl-sheet?${new URLSearchParams({
		applicationId,
		propertyId,
	})}`
}

/**
 * Returns the ordered list of files to package, root-first. The caller
 * fetches each `url` best-effort and writes any present blob at `path`.
 */
export function planHouseholdZip(
	input: HouseholdZipPlanInput,
): HouseholdZipEntry[] {
	const {
		members,
		entryApplicationId,
		groupId,
		propertyId,
		includeFamilyReport,
	} = input
	const entries: HouseholdZipEntry[] = []

	if (includeFamilyReport) {
		entries.push({
			path: 'Family-Report.pdf',
			url: reportUrl({
				reportType: 'family',
				applicationId: entryApplicationId,
				propertyId,
				...(groupId ? { groupId } : {}),
			}),
		})
	}

	const ordered = [...members].sort(
		(a, b) => typeRank(a.applicationType) - typeRank(b.applicationType),
	)

	for (const member of ordered) {
		const folder = memberFolderName(member.displayName)
		entries.push({
			path: `${folder}/Individual-Report.pdf`,
			url: reportUrl({
				reportType: 'individual',
				applicationId: member.applicationId,
				propertyId,
			}),
		})
		entries.push({
			path: `${folder}/Applicant-Information.pdf`,
			url: reportUrl({
				reportType: 'applicant-info',
				applicationId: member.applicationId,
				propertyId,
			}),
		})
		entries.push({
			path: `${folder}/Income-Verification.pdf`,
			url: verificationUrl(member.applicationId, propertyId, 'income'),
		})
		entries.push({
			path: `${folder}/Rent-Verification.pdf`,
			url: verificationUrl(member.applicationId, propertyId, 'rent'),
		})
		entries.push({
			path: `${folder}/ETL-Sheet.xls`,
			url: etlUrl(member.applicationId, propertyId),
		})
	}

	return entries
}
